import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { usePreferences } from '../hooks/usePreferences'
import { showToast } from '../lib/toastStore'

// ─── Types ────────────────────────────────────────────────────────────────────

interface ExpiringItem {
  id:                      string
  name:                    string
  emoji:                   string | null
  current_expiration_date: string
  daysLeft:                number
}

interface SuggestedMeal {
  source:       'suggestion'
  name:         string
  description:  string
  time:         string
  uses:         string[]
  missing:      string[]
  instructions: string
}

interface SavedRecipe {
  id:          string
  name:        string
  ingredients: { name: string; amount: string; category: string }[]
}

interface PlannedMeal {
  name:          string
  source:        'suggestion' | 'recipe' | 'custom'
  description?:  string
  time?:         string
  uses?:         string[]
  missing?:      string[]
  instructions?: string
}

interface PantryItem {
  id:   string
  name: string
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getCategoryEmoji(name: string): string {
  const n = name.toLowerCase()
  if (['milk', 'cheese', 'yogurt', 'butter', 'cream'].some(w => n.includes(w))) return '🥛'
  if (['chicken', 'beef', 'pork', 'fish', 'salmon', 'meat'].some(w => n.includes(w))) return '🥩'
  if (['tomato', 'lettuce', 'spinach', 'onion', 'garlic', 'pepper', 'carrot'].some(w => n.includes(w))) return '🥬'
  if (['bread', 'flour', 'pasta', 'rice', 'noodle'].some(w => n.includes(w))) return '🍞'
  return '🍴'
}

function matchIngredient(name: string, pantryItems: PantryItem[]): boolean {
  const ing = name.toLowerCase().trim()
  return pantryItems.some(p => {
    const n = p.name.toLowerCase()
    return n === ing || n.includes(ing) || ing.includes(n) ||
      n.includes(ing.replace(/s$/, '')) || ing.includes(n.replace(/s$/, ''))
  })
}

// ─── Meal Picker Sheet ────────────────────────────────────────────────────────

function MealPickerSheet({
  day,
  suggestions,
  savedRecipes,
  pantryItems,
  onPick,
  onClose,
}: {
  day:          string
  suggestions:  SuggestedMeal[]
  savedRecipes: SavedRecipe[]
  pantryItems:  PantryItem[]
  onPick:       (meal: PlannedMeal) => void
  onClose:      () => void
}) {
  const [tab,        setTab]        = useState<'suggestions' | 'recipes' | 'custom'>('suggestions')
  const [customName, setCustomName] = useState('')

  function pickSuggestion(meal: SuggestedMeal) {
    onPick({ ...meal, source: 'suggestion' })
  }

  function pickRecipe(recipe: SavedRecipe) {
    const missing = recipe.ingredients
      .filter(ing => !matchIngredient(ing.name, pantryItems))
      .map(ing => ing.name)
    const uses = recipe.ingredients
      .filter(ing => matchIngredient(ing.name, pantryItems))
      .map(ing => ing.name)
    onPick({ name: recipe.name, source: 'recipe', uses, missing })
  }

  function pickCustom() {
    if (!customName.trim()) return
    onPick({ name: customName.trim(), source: 'custom' })
  }

  const tabs = [
    { id: 'suggestions', label: '✨ Suggestions', count: suggestions.length },
    { id: 'recipes',     label: '📖 Saved',       count: savedRecipes.length },
    { id: 'custom',      label: '✏️ Custom',       count: null },
  ] as const

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end items-center">
      <motion.div
        className="absolute inset-0 bg-black/40"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        onClick={onClose}
      />
      <motion.div
        className="relative bg-[#FAF8F4] rounded-t-3xl shadow-2xl w-full max-w-[480px]
                   max-h-[80vh] flex flex-col"
        initial={{ y: '100%' }} animate={{ y: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-[#E8E2DC]" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-2 pb-3 shrink-0">
          <h3 className="text-base font-semibold text-[#2C2A26]">Add meal for {day}</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F0EDE8] text-[#7A7468] text-sm"
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-5 pb-3 shrink-0">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className="flex-1 py-2 rounded-xl text-xs font-semibold transition-all"
              style={{
                background: tab === t.id ? '#6B9E78' : '#F2EDE6',
                color:      tab === t.id ? 'white'   : '#7A7468',
              }}
            >
              {t.label}
              {t.count !== null && t.count > 0 && (
                <span className="ml-1 opacity-70">({t.count})</span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 pb-6">

          {/* Suggestions tab */}
          {tab === 'suggestions' && (
            <div className="flex flex-col gap-2">
              {suggestions.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-[#9A9491]">No suggestions yet</p>
                  <p className="text-xs text-[#B0A99F] mt-1">
                    Tap "Get meal suggestions" above to generate ideas
                  </p>
                </div>
              ) : (
                suggestions.map((meal, i) => (
                  <button
                    key={i}
                    onClick={() => pickSuggestion(meal)}
                    className="w-full text-left bg-white rounded-2xl border border-[#F0EDE8]
                               p-4 hover:border-[#6B9E78]/30 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <p className="text-sm font-semibold text-[#2C2A26]">{meal.name}</p>
                      <span className="text-[10px] text-[#9A9491] bg-[#F2EDE6] px-2 py-0.5 rounded-full shrink-0">
                        ⏱ {meal.time}
                      </span>
                    </div>
                    <p className="text-xs text-[#7A7468] mb-2">{meal.description}</p>
                    <div className="flex gap-1.5 flex-wrap">
                      {meal.uses?.slice(0, 4).map(u => (
                        <span key={u} className="text-[10px] px-2 py-0.5 rounded-full bg-[#E8F2EA] text-[#6B9E78]">
                          ✓ {u}
                        </span>
                      ))}
                      {meal.missing?.length > 0 && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FAEAE8] text-[#C25B4E]">
                          +{meal.missing.length} to buy
                        </span>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}

          {/* Saved recipes tab */}
          {tab === 'recipes' && (
            <div className="flex flex-col gap-2">
              {savedRecipes.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-[#9A9491]">No saved recipes yet</p>
                  <p className="text-xs text-[#B0A99F] mt-1">
                    Save recipes from the Recipes page to use them here
                  </p>
                </div>
              ) : (
                savedRecipes.map(recipe => {
                  const missing = recipe.ingredients
                    .filter(ing => !matchIngredient(ing.name, pantryItems))
                  const have = recipe.ingredients.length - missing.length
                  return (
                    <button
                      key={recipe.id}
                      onClick={() => pickRecipe(recipe)}
                      className="w-full text-left bg-white rounded-2xl border border-[#F0EDE8]
                                 p-4 hover:border-[#6B9E78]/30 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-[#FAF8F4] flex items-center
                                        justify-center text-xl shrink-0">
                          🍳
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-[#2C2A26] truncate">{recipe.name}</p>
                          <p className="text-xs text-[#9A9491] mt-0.5">
                            {recipe.ingredients.length} ingredients ·{' '}
                            <span className="text-[#6B9E78]">{have} in pantry</span>
                            {missing.length > 0 && (
                              <span className="text-[#C25B4E]"> · {missing.length} needed</span>
                            )}
                          </p>
                        </div>
                      </div>
                    </button>
                  )
                })
              )}
            </div>
          )}

          {/* Custom tab */}
          {tab === 'custom' && (
            <div className="flex flex-col gap-3">
              <p className="text-xs text-[#9A9491]">
                Add any meal by name — great for family favorites or simple meals
              </p>
              <input
                autoFocus
                type="text"
                value={customName}
                onChange={e => setCustomName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && pickCustom()}
                placeholder="e.g. Spaghetti Bolognese"
                className="w-full px-4 py-3 rounded-xl border border-[#E8E2DC] text-sm
                           text-[#2C2A26] outline-none focus:border-[#6B9E78] bg-white"
              />
              <button
                onClick={pickCustom}
                disabled={!customName.trim()}
                className="w-full py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-40"
                style={{ background: '#6B9E78' }}
              >
                Add "{customName || 'meal'}" to {day}
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  )
}

// ─── MealPlanning ─────────────────────────────────────────────────────────────

export default function MealPlanning() {
  const navigate      = useNavigate()
  const { user }      = useAuth()
  const { isPremium } = usePreferences()
  const queryClient   = useQueryClient()

  const [suggestions,  setSuggestions]  = useState<SuggestedMeal[]>([])
  const [loading,      setLoading]      = useState(false)
  const [weekPlan,     setWeekPlan]     = useState<Record<string, PlannedMeal | null>>({
    Mon: null, Tue: null, Wed: null, Thu: null, Fri: null, Sat: null, Sun: null,
  })
  const [addingToList, setAddingToList] = useState(false)
  const [activeDay,    setActiveDay]    = useState<string | null>(null)

  useEffect(() => { document.title = 'Meal Planning — Pantry' }, [])

  // ── Resolve household ─────────────────────────────────────────────────────

  const { data: householdId } = useQuery({
    queryKey: ['householdId', user?.id],
    enabled:  !!user?.id,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('users').select('household_id').eq('id', user!.id).single()
      if (error) throw error
      return data.household_id as string
    },
  })

  // ── Expiring items ────────────────────────────────────────────────────────

  const { data: expiringItems } = useQuery<ExpiringItem[]>({
    queryKey: ['expiring-items', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const in7days = new Date()
      in7days.setDate(in7days.getDate() + 7)
      const { data } = await supabase
        .from('pantry_items')
        .select('id, name, emoji, current_expiration_date, track_expiration')
        .eq('household_id', householdId!)
        .eq('is_active', true)
        .eq('track_expiration', true)
        .not('current_expiration_date', 'is', null)
        .lte('current_expiration_date', in7days.toISOString().split('T')[0])
        .order('current_expiration_date', { ascending: true })
      return (data || []).map(item => ({
        ...item,
        daysLeft: Math.floor(
          (new Date(item.current_expiration_date!).getTime() - Date.now()) /
          (1000 * 60 * 60 * 24)
        ),
      }))
    },
  })

  // ── All pantry items ──────────────────────────────────────────────────────

  const { data: pantryItemsRaw = [] } = useQuery({
    queryKey: ['pantry-items-meal', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const { data } = await supabase
        .from('pantry_items')
        .select('id, name, emoji')
        .eq('household_id', householdId!)
        .eq('is_active', true)
      return (data || []) as PantryItem[]
    },
  })

  // ── Saved recipes ─────────────────────────────────────────────────────────

  const { data: savedRecipes = [] } = useQuery({
    queryKey: ['saved-recipes', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const { data } = await supabase
        .from('saved_recipes')
        .select('id, name, ingredients')
        .eq('household_id', householdId!)
        .order('created_at', { ascending: false })
      return (data ?? []) as SavedRecipe[]
    },
  })

  // ── Get Claude suggestions ────────────────────────────────────────────────

  const handleGetMeals = async () => {
    if (!expiringItems?.length) return
    setLoading(true)
    try {
      const response = await fetch('/api/claude-meals', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          expiringItems,
          pantryItems: pantryItemsRaw,
        }),
      })
      const data = await response.json()
      setSuggestions((data.meals || []).map((m: any) => ({ ...m, source: 'suggestion' })))
    } catch (err) {
      console.error('Meal planning error:', err)
      showToast('Could not get meal suggestions')
    } finally {
      setLoading(false)
    }
  }

  // ── Week plan actions ─────────────────────────────────────────────────────

  function assignMeal(day: string, meal: PlannedMeal) {
    setWeekPlan(prev => ({ ...prev, [day]: meal }))
    setActiveDay(null)
  }

  function removeFromDay(day: string) {
    setWeekPlan(prev => ({ ...prev, [day]: null }))
  }

  // ── Derived — all missing ingredients across week ─────────────────────────

  const allMissing = useMemo(() => {
    return Array.from(new Set(
      Object.values(weekPlan)
        .filter(Boolean)
        .flatMap(m => m!.missing || [])
    ))
  }, [weekPlan])

  const plannedCount = Object.values(weekPlan).filter(Boolean).length

  // ── Add missing to shopping list ──────────────────────────────────────────

  async function handleAddAllToList() {
    if (!householdId || allMissing.length === 0) return
    setAddingToList(true)
    try {
      let { data: activeList } = await supabase
        .from('shopping_lists')
        .select('id')
        .eq('household_id', householdId)
        .is('completed_at', null)
        .order('generated_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (!activeList) {
        const { data: newList } = await supabase
          .from('shopping_lists')
          .insert({ household_id: householdId, generated_at: new Date().toISOString() })
          .select('id').single()
        activeList = newList
      }
      if (!activeList) throw new Error('Could not create list')

      let addedCount = 0
      for (const name of allMissing) {
        const { data: existing } = await supabase
          .from('pantry_items')
          .select('id')
          .eq('household_id', householdId)
          .ilike('name', name)
          .limit(1)
          .maybeSingle()

        let itemId = existing?.id

        if (!itemId) {
          const { data: newItem } = await supabase
            .from('pantry_items')
            .insert({
              household_id:       householdId,
              name:               name.charAt(0).toUpperCase() + name.slice(1),
              emoji:              getCategoryEmoji(name),
              display_category:   'pantry',
              freshness_category: 'pantry_staple',
              is_active:          true,
              confirmation_count: 0,
              location:           'pantry',
              track_expiration:   false,
              status_override:    null,
            })
            .select('id').single()
          itemId = newItem?.id
        } else {
          await supabase.from('pantry_items').update({ status_override: null }).eq('id', itemId)
        }

        if (!itemId) continue

        const { data: onList } = await supabase
          .from('list_items')
          .select('id')
          .eq('list_id', activeList.id)
          .eq('item_id', itemId)
          .neq('status', 'removed')
          .maybeSingle()

        if (!onList) {
          await supabase.from('list_items').insert({
            list_id:       activeList.id,
            item_id:       itemId,
            status:        'pending',
            urgency_score: 50,
            sort_order:    0,
            is_one_off:    true,
          })
          addedCount++
        }
      }

      queryClient.invalidateQueries({ queryKey: ['pantry-items', householdId] })
      queryClient.invalidateQueries({ queryKey: ['latestList'] })
      queryClient.invalidateQueries({ queryKey: ['listItems'] })
      showToast(`${addedCount} ingredient${addedCount !== 1 ? 's' : ''} added to your list ✓`)
    } catch (err: any) {
      showToast('Something went wrong')
    } finally {
      setAddingToList(false)
    }
  }

  // ── Pantry + gate ──────────────────────────────────────────────────────────

  if (!isPremium) {
    return (
      <div className="min-h-screen bg-[#FAF8F4] flex flex-col max-w-[480px] mx-auto">
        <div className="flex items-center gap-3 px-4 py-4 border-b border-[#F0EDE8]">
          <button onClick={() => navigate(-1)} className="text-lg text-[#7A7468]">←</button>
          <p className="font-semibold text-[#2C2A26]">Meal Planning</p>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center px-6 gap-4 text-center">
          <span className="text-5xl">🍳</span>
          <p className="text-lg font-semibold text-[#2C2A26]">AI Meal Planning</p>
          <p className="text-sm text-[#7A7468]">
            Plan your week with AI suggestions, saved recipes, and custom meals.
          </p>
          <button
            onClick={() => navigate('/paywall')}
            className="w-full py-3.5 rounded-2xl text-white font-semibold text-sm mt-2"
            style={{ background: '#8B6914' }}
          >
            Unlock with Pantry +
          </button>
        </div>
      </div>
    )
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[#FAF8F4] max-w-[480px] mx-auto pb-12">

      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-4 border-b border-[#F0EDE8]">
        <button onClick={() => navigate(-1)} className="text-lg text-[#7A7468]">←</button>
        <div>
          <p className="font-semibold text-[#2C2A26]">Meal Planning</p>
          <p className="text-xs text-[#7A7468]">Plan your week, auto-fill your list</p>
        </div>
      </div>

      <div className="px-4 pt-5">

        {/* Expiring items */}
        {expiringItems && expiringItems.length > 0 && (
          <div className="mb-5">
            <p className="text-xs font-semibold text-[#9A9491] uppercase tracking-wide mb-3">
              Expiring this week
            </p>
            <div className="flex flex-wrap gap-2">
              {expiringItems.map(item => (
                <div key={item.id}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FAEAE8] border border-[#C25B4E]/20">
                  <span className="text-sm">{item.emoji}</span>
                  <span className="text-xs font-medium text-[#C25B4E]">{item.name}</span>
                  <span className="text-[10px] text-[#C25B4E]/70">
                    {item.daysLeft <= 0 ? 'today' : item.daysLeft === 1 ? 'tomorrow' : `${item.daysLeft}d`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Get suggestions button */}
        <button
          onClick={handleGetMeals}
          disabled={loading}
          className="w-full py-3.5 rounded-2xl text-sm font-semibold text-white disabled:opacity-50 mb-6"
          style={{ background: '#6B9E78' }}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
              Finding meal ideas…
            </span>
          ) : suggestions.length > 0
            ? `🔄 Refresh suggestions (${suggestions.length} loaded)`
            : expiringItems?.length
            ? '✨ Get AI meal suggestions'
            : '✨ Plan this week\'s meals'}
        </button>

        {/* ── Week Planner ── */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-[#9A9491] uppercase tracking-wide">
              This week
            </p>
            {plannedCount > 0 && (
              <p className="text-xs text-[#6B9E78] font-medium">
                {plannedCount}/7 planned
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            {DAYS.map(day => {
              const assigned = weekPlan[day]
              const sourceIcon = assigned?.source === 'recipe' ? '📖'
                : assigned?.source === 'custom' ? '✏️' : '✨'

              return (
                <div key={day} className="bg-white rounded-2xl border border-[#F0EDE8] overflow-hidden">
                  {assigned ? (
                    <div className="flex items-center gap-3 px-4 py-3">
                      <div className="w-10 shrink-0 text-center">
                        <p className="text-xs font-bold text-[#6B9E78]">{day}</p>
                      </div>
                      <span className="text-base shrink-0">{sourceIcon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#2C2A26] truncate">{assigned.name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {assigned.time && (
                            <p className="text-xs text-[#9A9491]">⏱ {assigned.time}</p>
                          )}
                          {assigned.missing && assigned.missing.length > 0 && (
                            <p className="text-xs text-[#C25B4E]">
                              · {assigned.missing.length} to buy
                            </p>
                          )}
                          {assigned.missing && assigned.missing.length === 0 && (
                            <p className="text-xs text-[#6B9E78]">· All in pantry ✓</p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => removeFromDay(day)}
                        className="w-6 h-6 rounded-full bg-[#F2EDE6] flex items-center
                                   justify-center text-[#9A9491] text-xs shrink-0"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setActiveDay(day)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left"
                    >
                      <div className="w-10 shrink-0 text-center">
                        <p className="text-xs font-bold text-[#9A9491]">{day}</p>
                      </div>
                      <p className="text-xs text-[#B0A99F] flex-1">+ Add meal</p>
                      <span className="text-[#E8E2DC] text-xs">→</span>
                    </button>
                  )}
                </div>
              )
            })}
          </div>

          {/* Missing summary + add to list */}
          {allMissing.length > 0 && (
            <div className="mt-4 bg-white rounded-2xl border border-[#F0EDE8] p-4">
              <p className="text-xs font-semibold text-[#9A9491] uppercase tracking-wide mb-3">
                Need to buy for this week
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                {allMissing.map(ing => (
                  <span key={ing} className="text-xs px-2.5 py-1 rounded-full bg-[#F2EDE6] text-[#7A7468]">
                    {ing}
                  </span>
                ))}
              </div>
              <button
                onClick={handleAddAllToList}
                disabled={addingToList}
                className="w-full py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: '#6B9E78' }}
              >
                {addingToList
                  ? 'Adding…'
                  : `Add ${allMissing.length} ingredient${allMissing.length !== 1 ? 's' : ''} to shopping list ✓`}
              </button>
            </div>
          )}

          {plannedCount > 0 && allMissing.length === 0 && (
            <div className="mt-4 bg-[#E8F2EA] rounded-2xl p-4 text-center">
              <p className="text-sm font-semibold text-[#6B9E78]">🎉 You have everything!</p>
              <p className="text-xs text-[#6B9E78]/70 mt-1">Your pantry covers the whole week</p>
            </div>
          )}
        </div>

        {/* Suggestions section (if loaded) */}
        {suggestions.length > 0 && (
          <div className="mb-6">
            <p className="text-xs font-semibold text-[#9A9491] uppercase tracking-wide mb-3">
              AI Suggestions
            </p>
            {suggestions.map((meal, i) => (
              <div key={i} className="bg-white rounded-2xl border border-[#E8E2DC] p-4 mb-3">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-[#2C2A26]">{meal.name}</p>
                    <p className="text-xs text-[#7A7468] mt-0.5">{meal.description}</p>
                  </div>
                  <span className="text-xs text-[#9A9491] bg-[#F2EDE6] px-2 py-1 rounded-full shrink-0">
                    ⏱ {meal.time}
                  </span>
                </div>

                {meal.instructions && (
                  <p className="text-xs text-[#7A7468] leading-relaxed mb-2 bg-[#FAF8F4] rounded-xl p-3">
                    {meal.instructions}
                  </p>
                )}

                <div className="flex gap-1.5 mb-2 flex-wrap">
                  {meal.uses?.map(u => (
                    <span key={u} className="text-xs px-2 py-0.5 rounded-full bg-[#E8F2EA] text-[#6B9E78]">
                      ✓ {u}
                    </span>
                  ))}
                  {meal.missing?.map(m => (
                    <span key={m} className="text-xs px-2 py-0.5 rounded-full bg-[#F2EDE6] text-[#7A7468]">
                      + {m}
                    </span>
                  ))}
                </div>

                {/* Quick assign buttons */}
                <div className="flex gap-1.5 flex-wrap mt-2 pt-2 border-t border-[#F0EDE8]">
                  <span className="text-[10px] text-[#9A9491] self-center">Add to:</span>
                  {DAYS.filter(d => !weekPlan[d]).map(day => (
                    <button
                      key={day}
                      onClick={() => assignMeal(day, { ...meal, source: 'suggestion' })}
                      className="text-xs px-2.5 py-1 rounded-lg border border-[#6B9E78]/30
                                 text-[#6B9E78] font-medium hover:bg-[#E8F2EA] transition-colors"
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

      </div>

      {/* Meal picker sheet */}
      <AnimatePresence>
        {activeDay && (
          <MealPickerSheet
            day={activeDay}
            suggestions={suggestions}
            savedRecipes={savedRecipes}
            pantryItems={pantryItemsRaw}
            onPick={meal => assignMeal(activeDay, meal)}
            onClose={() => setActiveDay(null)}
          />
        )}
      </AnimatePresence>
    </div>
  )
}
